/** Vocal sound presets → the engine's real per-channel FX chain (EQ · compressor · limiter · reverb · delay). */
export const FX_KEYS = ["eq", "compressor", "limiter", "reverb", "delay"];

const fx = (eq, comp, limiter, reverb, delay) => ({
  eq: { on: Boolean(eq), low: eq?.[0] ?? 0, mid: eq?.[1] ?? 0, high: eq?.[2] ?? 0 },
  compressor: { on: Boolean(comp), threshold: comp?.[0] ?? -18, ratio: comp?.[1] ?? 3 },
  limiter: { on: limiter != null, threshold: limiter ?? -3 },
  reverb: { on: reverb != null, send: reverb ?? 0.15 },
  delay: { on: Boolean(delay), time: delay?.[0] ?? 0.3, feedback: delay?.[1] ?? 0.25 },
});

export const BUILTIN_PRESETS = [
  { key: "warm_gospel", label: "Warm Gospel Vocal", hint: "Smooth, rich, natural gospel singing", fx: fx([2, -1, 2], [-20, 3], -2, 0.22, null) },
  { key: "baritone", label: "Deep Male Baritone", hint: "Warm low presence, clear words, controlled compression", fx: fx([3.5, -2, 2.5], [-22, 4], -2, 0.12, null) },
  { key: "rnb_gospel", label: "R&B Gospel", hint: "Smooth vocals, gentle reverb, polished dynamics", fx: fx([1, -1.5, 3.5], [-24, 3.5], -1.5, 0.3, [0.32, 0.18]) },
  { key: "reggae_gospel", label: "Reggae Gospel", hint: "Clear lead, controlled bass, tasteful delay", fx: fx([-3, 1.5, 2], [-18, 3], -2, 0.12, [0.375, 0.35]) },
  { key: "choir", label: "Gospel Choir", hint: "Balanced group vocals with spacious harmony", fx: fx([-1, -2, 2.5], [-20, 2.5], -3, 0.4, null) },
  { key: "preaching", label: "Preaching Voice", hint: "Warm speech clarity, steady dynamics", fx: fx([1.5, 2, 1], [-26, 5], -2, 0.05, null) },
];
export const NO_FX = fx(null, null, null, null, null);

export const presetByKey = (key, custom = []) =>
  BUILTIN_PRESETS.find((p) => p.key === key) || custom.map((c) => ({ key: `custom:${c.id}`, label: c.name, fx: c.fx })).find((p) => p.key === key) || null;

const ENGINE_PARAMS = {
  eq: (p) => ({ low: p.low, mid: p.mid, high: p.high }),
  compressor: (p) => ({ threshold: p.threshold, ratio: p.ratio }),
  limiter: (p) => ({ threshold: p.threshold, ratio: 20 }),
  reverb: (p) => ({ send: p.send }),
  delay: (p) => ({ time: p.time, feedback: p.feedback }),
};

/**
 * Push a vocal sound onto an engine channel. Reverb / delay only reach the speakers in the engine's Wet mode, so it
 * switches Wet on when they're used. → true when it had to switch the studio to Wet.
 */
export function applyFx(engine, idx, sound) {
  const ch = engine?.channels?.[idx];
  if (!ch || !sound) return false;
  FX_KEYS.forEach((k) => {
    ch.fxParams[k] = { ...ch.fxParams[k], ...ENGINE_PARAMS[k](sound[k]) };
    engine.setChannelEffect(idx, k, Boolean(sound[k].on));
  });
  if ((sound.reverb.on || sound.delay.on) && engine.dryMode) { engine.setDryMode(false); return true; }
  return false;
}
